import React from 'react';
import {
  CATEGORIES,
  SUBCATEGORIES,
  FALLBACK_CATEGORY_ICONS,
  isChassisCategory,
  isShippingCategory,
  isAdminMode
} from './constants.js';

// Re-export isLockedProduct from constants for backward compatibility
export { isLockedProduct } from './constants.js';

// ============================================================================
// API Configuration - FIXED: Increased limit from 200 to 300 to include all modules
// ============================================================================

export const API_CONFIG = {
  baseUrl: 'https://www.vesaro.com/_functions/test?limit=300',  // CHANGED: Was 200, now 300
  timeout: 10000,
  validateSlug: async (slug) => {
    try {
      const response = await fetch(`https://www.vesaro.com/_functions/test?limit=1&slug=${slug}`);
      if (response.ok) {
        const data = await response.json();
        return data.baseConfig && data.baseConfig.slug === slug;
      }
      return false;
    } catch (error) {
      return false;
    }
  }
};

// ============================================================================
// STRING UTILITIES
// ============================================================================

// Helper function to clean product names - removes non-printable and weird Unicode characters
export const cleanProductName = (name) => {
  if (!name) return '';
  return name.replace(/[^\x20-\x7E\u00A0-\u017F\u0100-\u024F]/g, '').trim();
};

// Helper function to extract text from Wix rich text objects
export const extractRichText = (richText) => {
  if (!richText) return '';
  
  // If it's already a string, return it
  if (typeof richText === 'string') {
    return richText;
  }
  
  // If it's not an object, convert to string
  if (typeof richText !== 'object') {
    return String(richText);
  }
  
  // Handle Wix rich text object structure
  // Wix rich text can have different formats:
  
  // Format 1: { nodes: [...] } structure
  if (richText.nodes && Array.isArray(richText.nodes)) {
    return extractTextFromNodes(richText.nodes);
  }
  
  // Format 2: { text: "..." } simple structure
  if (richText.text) {
    return richText.text;
  }
  
  // Format 3: { html: "..." } HTML structure
  if (richText.html) {
    // Strip HTML tags and return plain text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = richText.html;
    return tempDiv.textContent || tempDiv.innerText || '';
  }
  
  // Format 4: { content: "..." } content structure
  if (richText.content) {
    if (typeof richText.content === 'string') {
      return richText.content;
    }
    return extractRichText(richText.content);
  }
  
  // Fallback: try to stringify and check if it's meaningful
  try {
    const str = JSON.stringify(richText);
    // If it's just an empty object or simple structure, return empty
    if (str === '{}' || str === '[]') return '';
    
    // Last resort: return empty string rather than [object Object]
    return '';
  } catch (e) {
    return '';
  }
};

// Helper to recursively extract text from Wix rich text nodes
const extractTextFromNodes = (nodes) => {
  if (!Array.isArray(nodes)) return '';
  
  return nodes.map(node => {
    // Text node
    if (node.type === 'text' && node.text) {
      return node.text;
    }
    
    // Paragraph or other container with children
    if (node.nodes && Array.isArray(node.nodes)) {
      return extractTextFromNodes(node.nodes);
    }
    
    // Children array (alternative structure)
    if (node.children && Array.isArray(node.children)) {
      return extractTextFromNodes(node.children);
    }
    
    // Direct text property
    if (node.text) {
      return node.text;
    }
    
    // Value property (some Wix formats)
    if (node.value) {
      return typeof node.value === 'string' ? node.value : '';
    }
    
    return '';
  }).join(' ').replace(/\s+/g, ' ').trim();
};

// ============================================================================
// SECURITY UTILITIES - EXPORTED
// ============================================================================

// Sanitize user input to prevent XSS
export const sanitizeInput = (input) => {
  if (!input) return '';
  if (typeof input !== 'string') return String(input);
  
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers like onclick=
    .replace(/script/gi, 'scrpt') // Break script tags
    .trim();
};

// Validate URL parameters to prevent injection
export const validateUrlParam = (param, type = 'slug') => {
  if (!param) return null;
  
  const patterns = {
    slug: /^[a-zA-Z0-9-_]+$/,
    id: /^[a-zA-Z0-9]+$/,
    build: /^[a-zA-Z0-9-,_]+$/
  };
  
  const pattern = patterns[type] || patterns.slug;
  
  if (!pattern.test(param)) {
    return null;
  }

  // Also limit length to prevent overflow attacks
  const maxLength = type === 'build' ? 2000 : 200;
  if (param.length > maxLength) {
    return null;
  }
  
  return param;
};

// Escape HTML to prevent XSS in rendered content
export const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Category sorting - respects CMS categoryOrder
export const sortCategoriesWithConfig = (categories, categoryConfig) => {
  return [...categories].sort((a, b) => {
    // Business rule: Chassis always first (unless in admin mode)
    if (!isAdminMode()) {
      if (isChassisCategory(a) && !isChassisCategory(b)) {
        return -1;
      }
      if (isChassisCategory(b) && !isChassisCategory(a)) {
        return 1;
      }
    }
    
    // Use CMS categoryOrder values
    const aOrder = categoryConfig[a]?.categoryOrder;
    const bOrder = categoryConfig[b]?.categoryOrder;
    
    // If both have CMS order, use that
    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }
    
    // If only one has CMS order, prioritize it
    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;
    
    // If neither has CMS order, alphabetical
    return a.localeCompare(b);
  });
};

// Business logic requirements - UPDATED: Chassis and Shipping only locked for non-admin users
export const isLockedCategory = (categoryName) => {
  // In admin mode, nothing is locked
  if (isAdminMode()) {
    return false;
  }

  // For regular users, chassis and shipping are locked
  return isChassisCategory(categoryName) || isShippingCategory(categoryName);
};

export { isChassisCategory, isShippingCategory }; // Re-export from constants

export const isChassisProduct = (product, mainCategory, subCategory) => {
  return isChassisCategory(mainCategory) || isChassisCategory(subCategory);
};

// Flight simulator detection
export const isFlightSimulator = (baseSimulator) => {
  if (!baseSimulator) return false;
  
  // Check Default Style field first (preferred method)
  const defaultStyle = baseSimulator.defaultStyle || baseSimulator['Default Style'];
  if (defaultStyle) {
    try {
      const styles = typeof defaultStyle === 'string' ? JSON.parse(defaultStyle) : defaultStyle;
      if (Array.isArray(styles) && styles.includes('Flight')) {
        return true;
      }
    } catch (e) {
      // Parsing error
    }
  }
  
  // Fallback to name detection
  const name = (baseSimulator.name || baseSimulator['Product Name'] || '').toLowerCase();
  const slug = (baseSimulator.slug || '').toLowerCase();
  
  return name.includes('flight') || name.includes('multi-flight') || 
         slug.includes('flight') || slug.includes('multi-flight');
};

// Icon handling with CMS support - Fixed for General inheritance
export const getIconForCategory = (categoryName, categoryConfig = {}, parentCategoryName = null) => {
  if (!categoryName || typeof categoryName !== 'string') {
    return '⚙️';
  }
  
  // Sanitize category names
  const safeCategoryName = sanitizeInput(categoryName);
  const safeParentName = parentCategoryName ? sanitizeInput(parentCategoryName) : null;
  
  // SPECIAL CASE: If this is "General" subcategory and parent is provided, ALWAYS use parent's icon
  if (safeCategoryName === SUBCATEGORIES.GENERAL && safeParentName) {
    const parentConfigData = categoryConfig[safeParentName];
    
    if (parentConfigData?.icon) {
      if (typeof parentConfigData.icon === 'string' && parentConfigData.icon.startsWith('http')) {
        return (
          <img 
            src={parentConfigData.icon} 
            alt={safeParentName}
            className="w-full h-full object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.insertAdjacentText('beforeend', '⚙️');
            }}
          />
        );
      }
      
      if (typeof parentConfigData.icon === 'string') {
        return parentConfigData.icon;
      }
    }
  }
  
  // Check if we have an icon from CMS config (for non-General categories)
  const configData = categoryConfig[safeCategoryName];
  
  if (configData?.icon) {
    // If icon is already a processed URL from backend
    if (typeof configData.icon === 'string' && configData.icon.startsWith('http')) {
      // Validate URL to prevent XSS
      try {
        const url = new URL(configData.icon);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
          return '⚙️';
        }
      } catch (e) {
        return '⚙️';
      }
      
      return (
        <img 
          src={configData.icon} 
          alt={safeCategoryName}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.insertAdjacentText('beforeend', '⚙️');
          }}
        />
      );
    }
    
    // If icon is emoji or text
    if (typeof configData.icon === 'string') {
      return sanitizeInput(configData.icon);
    }
  }
  
  // Fallback to hardcoded icons for specific categories
  const fallbackIcon = FALLBACK_CATEGORY_ICONS[safeCategoryName];
  if (fallbackIcon) {
    return fallbackIcon;
  }
  
  // Smart fallbacks based on category name keywords
  const lowerName = safeCategoryName.toLowerCase();
  let smartFallback = '⚙️'; // default
  
  if (lowerName.includes('display')) {
    if (lowerName.includes('single')) smartFallback = '🖥️';
    else if (lowerName.includes('triple')) smartFallback = '🖥️';
    else if (lowerName.includes('stand')) smartFallback = '📺';
    else smartFallback = '🖥️';
  } else if (lowerName.includes('vr') || lowerName.includes('headset')) {
    smartFallback = '🥽';
  } else if (lowerName.includes('wheel')) {
    if (lowerName.includes('rim')) smartFallback = '⚪';
    else if (lowerName.includes('base')) smartFallback = '🔘';
    else smartFallback = '🎮';
  } else if (lowerName.includes('seat')) {
    smartFallback = '🪑';
  } else if (lowerName.includes('audio') || lowerName.includes('speaker') || lowerName.includes('headphone')) {
    if (lowerName.includes('headphone')) smartFallback = '🎧';
    else smartFallback = '🔊';
  } else if (lowerName.includes('motion') || lowerName.includes('platform')) {
    smartFallback = '🎢';
  } else if (lowerName.includes('pedal')) {
    smartFallback = '🦶';
  } else if (lowerName.includes('control') || lowerName.includes('handbrake') || lowerName.includes('gear')) {
    if (lowerName.includes('handbrake')) smartFallback = '🅿️';
    else if (lowerName.includes('gear')) smartFallback = '⚙️';
    else smartFallback = '🎮';
  } else if (lowerName.includes('chassis')) {
    smartFallback = '🏗️';
  } else if (lowerName.includes('pc') || lowerName.includes('computer')) {
    smartFallback = '💻';
  } else if (lowerName.includes('ups') || lowerName.includes('power')) {
    smartFallback = '🔋';
  } else if (lowerName.includes('accessory') || lowerName.includes('upgrade')) {
    smartFallback = '🔧';
  }
  
  return smartFallback;
};

export const getImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return null;
  }

  // Already a direct URL
  if (imageUrl.startsWith('https://download-files.wixmp.com/') || 
      imageUrl.startsWith('https://static.wixstatic.com/')) {
    return imageUrl;
  }

  // Wix image format (shouldn't happen if backend processed it, but as fallback)
  if (imageUrl.startsWith('wix:image://')) {
    const match = imageUrl.match(/wix:image:\/\/v1\/(.+?)\/(.+?)#/);
    if (match) {
      const [, hash, filename] = match;
      return `https://static.wixstatic.com/media/${hash}/${filename}`;
    }
  }

  // Any other HTTP(S) URL
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  return null;
};

// Price handling
export const parsePrice = (price) => {
  if (typeof price === 'number') return price;
  if (!price || typeof price !== 'string') return 0;
  const match = price.match(/[\d,]+\.?\d*/);
  return match ? parseFloat(match[0].replace(',', '')) : 0;
};

export const formatPrice = (price) => {
  const numPrice = parsePrice(price);
  return `£${numPrice.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Product key handling
export const createProductKey = (mainCategory, subCategory) => {
  return `${mainCategory}||${subCategory}`;
};

export const parseProductKey = (key) => {
  const separatorIndex = key.indexOf('||');
  return {
    mainCategory: key.substring(0, separatorIndex),
    subCategory: key.substring(separatorIndex + 2)
  };
};

// Minimal demo data for fallback
export const getDemoData = () => ({
  [CATEGORIES.CHASSIS]: {
    [SUBCATEGORIES.CHASSIS]: [
      { 
        name: 'Vesaro Stage 3 Chassis', 
        price: 1999.99, 
        id: 'chassis1', 
        mainCategory: CATEGORIES.CHASSIS, 
        subCategory: SUBCATEGORIES.CHASSIS, 
        description: 'Professional-grade racing chassis', 
        requires: [], 
        slug: 'vesaro-stage3-chassis', 
        brand: ['Vesaro'],
        isLocked: !isAdminMode() // Only locked for non-admin users
      }
    ]
  },
  'Demo Category': {
    'Demo Subcategory': [
      { 
        name: 'Demo Product', 
        price: 99.99, 
        id: 'demo1', 
        mainCategory: 'Demo Category', 
        subCategory: 'Demo Subcategory', 
        description: 'This is demo data - check your connection', 
        requires: [], 
        slug: 'demo-product', 
        brand: ['Demo'],
        isLocked: false 
      }
    ]
  }
});